/**
 * CitationGenerator - Academic Attribution Made Easy
 * 
 * "Standing on the shoulders of giants" - every academic knows citations matter.
 * This component generates proper MLA, APA, and Chicago citations for:
 * - Individual projects
 * - Entire project lineages (the whole "web" of connected work)
 * 
 * CC BY-SA 4.0 requires attribution - we make it effortless.
 *
 * SECURITY FIX (2026-05-01): Removed dangerouslySetInnerHTML from CitationText.
 * Citations contain user-controlled data (project names, usernames) that was being
 * rendered as raw HTML — a stored XSS vector. Replaced with SafeCitationText,
 * a pure React component that parses *italic* markers and "curly quotes" into
 * React elements without ever touching innerHTML.
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Copy, Check, BookOpen, FileText, Link2, ChevronDown,
  Download, Share2, GitBranch, Users, Calendar, Globe
} from 'lucide-react';
import { cn, formatDate, copyToClipboard } from '../../utils/cn';

// ============================================================================
// CITATION FORMAT CONFIGURATIONS
// ============================================================================

const CITATION_FORMATS = {
  mla: {
    id: 'mla',
    name: 'MLA 9th Edition',
    description: 'Modern Language Association',
    icon: BookOpen
  },
  apa: {
    id: 'apa',
    name: 'APA 7th Edition',
    description: 'American Psychological Association',
    icon: FileText
  },
  chicago: {
    id: 'chicago',
    name: 'Chicago 17th Edition',
    description: 'Chicago Manual of Style',
    icon: BookOpen
  },
  bibtex: {
    id: 'bibtex',
    name: 'BibTeX',
    description: 'For LaTeX documents',
    icon: FileText
  }
};

// ============================================================================
// CITATION GENERATION FUNCTIONS
// ============================================================================

/**
 * Generate MLA citation for a project
 */
function generateMLA(project, accessDate = new Date()) {
  const author = project.author?.username || project.author?.firstName 
    ? `${project.author.lastName || project.author.username}, ${project.author.firstName || ''}`
    : 'Unknown Author';
  
  const title = `"${project.name}"`;
  const container = 'Enume';
  const version = project.versionNumber ? `version ${project.versionNumber}` : '';
  const publisher = 'Enume Platform';
  const pubDate = formatDate(project.created_at || project.createdAt);
  const url = `${window.location.origin}/project/${project.id}`;
  const accessed = formatDate(accessDate);
  
  let citation = `${author}. ${title}. `;
  citation += `*${container}*, `;
  if (version) citation += `${version}, `;
  citation += `${publisher}, ${pubDate}, `;
  citation += `${url}. `;
  citation += `Accessed ${accessed}.`;
  
  return citation;
}

/**
 * Generate APA citation for a project
 */
function generateAPA(project, accessDate = new Date()) {
  const author = project.author?.username || 'Unknown';
  const year = new Date(project.created_at || project.createdAt).getFullYear();
  const title = project.name;
  const url = `${window.location.origin}/project/${project.id}`;
  const retrieved = formatDate(accessDate);
  
  let citation = `${author}. (${year}). `;
  citation += `*${title}*. `;
  citation += `Enume. `;
  citation += `Retrieved ${retrieved}, from ${url}`;
  
  return citation;
}

/**
 * Generate Chicago citation for a project
 */
function generateChicago(project, accessDate = new Date()) {
  const author = project.author?.username || project.author?.firstName
    ? `${project.author.firstName || ''} ${project.author.lastName || project.author.username}`
    : 'Unknown Author';
  
  const title = `"${project.name}"`;
  const pubDate = formatDate(project.created_at || project.createdAt);
  const url = `${window.location.origin}/project/${project.id}`;
  const accessed = formatDate(accessDate);
  
  let citation = `${author}. ${title}. `;
  citation += `Enume. ${pubDate}. `;
  citation += `${url} (accessed ${accessed}).`;
  
  return citation;
}

/**
 * Generate BibTeX entry for a project
 */
function generateBibTeX(project) {
  const author = project.author?.username || 'Unknown';
  const year = new Date(project.created_at || project.createdAt).getFullYear();
  const key = `${author.toLowerCase().replace(/\s+/g, '')}${year}${project.name.substring(0, 10).toLowerCase().replace(/\s+/g, '')}`;
  const url = `${window.location.origin}/project/${project.id}`;
  
  return `@misc{${key},
  author = {${author}},
  title = {${project.name}},
  year = {${year}},
  howpublished = {Enume},
  url = {${url}},
  note = {CC BY-SA 4.0 License}
}`;
}

/**
 * Generate citation for a project in specified format
 */
function generateCitation(project, format = 'mla') {
  switch (format) {
    case 'mla':
      return generateMLA(project);
    case 'apa':
      return generateAPA(project);
    case 'chicago':
      return generateChicago(project);
    case 'bibtex':
      return generateBibTeX(project);
    default:
      return generateMLA(project);
  }
}

/**
 * Generate citations for an entire lineage (web of projects)
 */
function generateLineageCitations(projects, format = 'mla') {
  // Sort by date (oldest first)
  const sorted = [...projects].sort((a, b) => 
    new Date(a.created_at || a.createdAt) - new Date(b.created_at || b.createdAt)
  );
  
  return sorted.map(project => generateCitation(project, format));
}

// ============================================================================
// SAFE CITATION TEXT RENDERER
// ============================================================================
//
// This replaces the old dangerouslySetInnerHTML approach.
// It parses the citation string into segments, converting:
//   *text* → <em>text</em>        (italic for container/title names)
//   "text" → \u201Ctext\u201D     (curly quotes for titles)
//
// All text content is rendered as React text nodes — never as raw HTML.
// User-controlled data (project names, usernames) stays inert.
//

/**
 * Parse a citation string into an array of React elements.
 * Handles *italic* markers and "straight quotes" → curly quotes.
 * All output is safe React elements — no innerHTML involved.
 */
function parseCitationToElements(citation) {
  const elements = [];
  let remaining = citation;
  let key = 0;

  while (remaining.length > 0) {
    // Find the next special marker: either * (italic) or " (quote)
    const italicIdx = remaining.indexOf('*');
    const quoteIdx = remaining.indexOf('"');

    // Determine which comes first
    let nextIdx = -1;
    let markerType = null;

    if (italicIdx === -1 && quoteIdx === -1) {
      // No more markers — push the rest as plain text
      elements.push(remaining);
      break;
    } else if (italicIdx === -1) {
      nextIdx = quoteIdx;
      markerType = 'quote';
    } else if (quoteIdx === -1) {
      nextIdx = italicIdx;
      markerType = 'italic';
    } else {
      // Both exist — take whichever comes first
      if (italicIdx < quoteIdx) {
        nextIdx = italicIdx;
        markerType = 'italic';
      } else {
        nextIdx = quoteIdx;
        markerType = 'quote';
      }
    }

    // Push any plain text before the marker
    if (nextIdx > 0) {
      elements.push(remaining.slice(0, nextIdx));
    }

    if (markerType === 'italic') {
      // Look for closing *
      const closeIdx = remaining.indexOf('*', nextIdx + 1);
      if (closeIdx === -1) {
        // No closing * — treat as literal text
        elements.push(remaining.slice(nextIdx));
        break;
      }
      const innerText = remaining.slice(nextIdx + 1, closeIdx);
      elements.push(<em key={`i${key++}`}>{innerText}</em>);
      remaining = remaining.slice(closeIdx + 1);
    } else {
      // Quote — look for closing "
      const closeIdx = remaining.indexOf('"', nextIdx + 1);
      if (closeIdx === -1) {
        // No closing " — treat as literal text
        elements.push(remaining.slice(nextIdx));
        break;
      }
      const innerText = remaining.slice(nextIdx + 1, closeIdx);
      elements.push(<span key={`q${key++}`}>{'\u201C'}{innerText}{'\u201D'}</span>);
      remaining = remaining.slice(closeIdx + 1);
    }
  }

  return elements;
}

// ============================================================================
// CITATION DISPLAY COMPONENT
// ============================================================================

const CitationText = ({ citation, format, onCopy }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    const success = await copyToClipboard(citation);
    if (success) {
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  // For BibTeX, use monospace formatting
  const isBibTeX = format === 'bibtex';

  // Parse citation into safe React elements (only for non-BibTeX)
  const renderedCitation = useMemo(() => {
    if (isBibTeX) return null;
    return parseCitationToElements(citation);
  }, [citation, isBibTeX]);
  
  return (
    <div className="relative group">
      <div className={cn(
        'p-4 bg-zinc-800/50 rounded-lg border border-zinc-700',
        'text-sm leading-relaxed',
        isBibTeX ? 'font-mono text-xs whitespace-pre' : 'text-zinc-300'
      )}>
        {isBibTeX ? citation : <span>{renderedCitation}</span>}
      </div>
      
      {/* Copy button */}
      <button
        onClick={handleCopy}
        className={cn(
          'absolute top-2 right-2 p-2 rounded-lg transition-all',
          'opacity-0 group-hover:opacity-100',
          copied 
            ? 'bg-emerald-500/20 text-emerald-400' 
            : 'bg-zinc-700 text-zinc-400 hover:text-white'
        )}
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
};

// ============================================================================
// FORMAT SELECTOR COMPONENT
// ============================================================================

const FormatSelector = ({ selectedFormat, onSelectFormat }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selected = CITATION_FORMATS[selectedFormat];
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
          'bg-zinc-800 border border-zinc-700 hover:border-zinc-600',
          'text-sm font-medium text-white'
        )}
      >
        <selected.icon className="w-4 h-4 text-purple-400" />
        {selected.name}
        <ChevronDown className={cn(
          'w-4 h-4 text-zinc-400 transition-transform',
          isOpen && 'rotate-180'
        )} />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)} 
          />
          <div className="absolute top-full left-0 mt-2 w-64 bg-zinc-900 border border-zinc-800 rounded-xl shadow-xl z-50 overflow-hidden">
            {Object.values(CITATION_FORMATS).map(format => (
              <button
                key={format.id}
                onClick={() => {
                  onSelectFormat(format.id);
                  setIsOpen(false);
                }}
                className={cn(
                  'w-full flex items-start gap-3 px-4 py-3 text-left transition-colors',
                  selectedFormat === format.id
                    ? 'bg-purple-500/10 text-purple-400'
                    : 'text-zinc-300 hover:bg-zinc-800'
                )}
              >
                <format.icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">{format.name}</p>
                  <p className="text-xs text-zinc-500">{format.description}</p>
                </div>
                {selectedFormat === format.id && (
                  <Check className="w-4 h-4 ml-auto text-purple-400" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ============================================================================
// MAIN CITATION GENERATOR COMPONENT
// ============================================================================

export const CitationGenerator = ({
  project,
  lineage = [], // Array of projects in the lineage
  showLineageCitations = false,
  onClose,
  className
}) => {
  const [format, setFormat] = useState('mla');
  const [view, setView] = useState('single'); // 'single' | 'lineage'
  const [copiedAll, setCopiedAll] = useState(false);
  
  // Generate citations
  const singleCitation = useMemo(() => 
    generateCitation(project, format),
    [project, format]
  );
  
  const lineageCitations = useMemo(() => 
    generateLineageCitations(lineage, format),
    [lineage, format]
  );
  
  // Combine all lineage citations for copy
  const allCitations = useMemo(() => {
    if (format === 'bibtex') {
      return lineageCitations.join('\n\n');
    }
    return lineageCitations.join('\n\n');
  }, [lineageCitations, format]);
  
  // Handle copy all
  const handleCopyAll = async () => {
    const success = await copyToClipboard(allCitations);
    if (success) {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    }
  };
  
  // Handle download as file
  const handleDownload = () => {
    const filename = format === 'bibtex' 
      ? `${project.name.replace(/\s+/g, '_')}_citations.bib`
      : `${project.name.replace(/\s+/g, '_')}_citations.txt`;
    
    const content = view === 'lineage' ? allCitations : singleCitation;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  return (
    <div className={cn('', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-purple-400" />
          <h3 className="text-lg font-semibold text-white">Cite This Work</h3>
        </div>
        
        <FormatSelector 
          selectedFormat={format} 
          onSelectFormat={setFormat} 
        />
      </div>
      
      {/* View toggle (if lineage available) */}
      {lineage.length > 1 && (
        <div className="flex items-center gap-2 mb-4 p-1 bg-zinc-800/50 rounded-lg">
          <button
            onClick={() => setView('single')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
              view === 'single'
                ? 'bg-zinc-700 text-white'
                : 'text-zinc-400 hover:text-white'
            )}
          >
            <FileText className="w-4 h-4" />
            This Project
          </button>
          <button
            onClick={() => setView('lineage')}
            className={cn(
              'flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors',
              view === 'lineage'
                ? 'bg-zinc-700 text-white'
                : 'text-zinc-400 hover:text-white'
            )}
          >
            <GitBranch className="w-4 h-4" />
            Entire Lineage ({lineage.length})
          </button>
        </div>
      )}
      
      {/* License notice */}
      <div className="flex items-center gap-2 mb-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
        <Globe className="w-4 h-4 text-purple-400 flex-shrink-0" />
        <p className="text-xs text-purple-300">
          This work is licensed under <strong>CC BY-SA 4.0</strong>. 
          You must give appropriate credit when using or remixing.
        </p>
      </div>
      
      {/* Citations */}
      {view === 'single' ? (
        <CitationText citation={singleCitation} format={format} />
      ) : (
        <div className="space-y-3">
          {/* Actions bar */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-zinc-400">
              {lineage.length} works in this creative lineage
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyAll}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  copiedAll
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-zinc-800 text-zinc-300 hover:text-white'
                )}
              >
                {copiedAll ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copiedAll ? 'Copied!' : 'Copy All'}
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-zinc-800 text-zinc-300 hover:text-white transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </button>
            </div>
          </div>
          
          {/* Lineage citations list */}
          <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
            {lineage.map((proj, idx) => (
              <div key={proj.id} className="relative">
                {/* Connection line */}
                {idx < lineage.length - 1 && (
                  <div className="absolute left-4 top-full w-0.5 h-2 bg-zinc-700" />
                )}
                
                <div className="flex items-start gap-3">
                  <div className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold',
                    proj.id === project.id
                      ? 'bg-purple-500 text-white'
                      : 'bg-zinc-800 text-zinc-400'
                  )}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white truncate mb-1">
                      {proj.name}
                      {proj.id === project.id && (
                        <span className="ml-2 text-xs text-purple-400">(current)</span>
                      )}
                    </p>
                    <CitationText 
                      citation={generateCitation(proj, format)} 
                      format={format}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Attribution reminder */}
      <div className="mt-4 pt-4 border-t border-zinc-800">
        <p className="text-xs text-zinc-500">
          <strong>Attribution required:</strong> When using this work, you must credit 
          the creator(s) and indicate if changes were made. Share alike - if you remix 
          or build upon this work, you must distribute under the same CC BY-SA 4.0 license.
        </p>
      </div>
    </div>
  );
};

// ============================================================================
// CITATION BUTTON (for use in ProjectDetailOverlay)
// ============================================================================

export const CiteButton = ({ onClick, className }) => (
  <button
    onClick={onClick}
    className={cn(
      'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
      'bg-zinc-800 text-zinc-300 hover:text-white hover:bg-zinc-700',
      'text-sm font-medium',
      className
    )}
  >
    <BookOpen className="w-4 h-4" />
    Cite
  </button>
);

// ============================================================================
// CITATION MODAL (standalone modal version)
// ============================================================================

export const CitationModal = ({ isOpen, onClose, project, lineage = [] }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-auto bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors z-10"
        >
          <span className="sr-only">Close</span>
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        
        <div className="p-6">
          <CitationGenerator 
            project={project} 
            lineage={lineage}
            showLineageCitations={lineage.length > 1}
          />
        </div>
      </div>
    </div>
  );
};

export default CitationGenerator;